import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography, Chip, IconButton, Container, Select, MenuItem, FormControl } from '@mui/material';
import DocumentsTab from './tabs/DocumentsTab';
import { useHistory, useParams } from 'react-router-dom';
import GeneratedReportTab from './tabs/GeneratedReportTab';
import DealRoomTab from './tabs/DealRoomTab';
import { DmProject, getDmProject, updateDmProject } from '../api-services/dm-projects.client';
import { mapDmProjectStatusToDealStatus, DEAL_REVIEW_STATUSES } from '../utils/constants';
import { useToast } from '../context/toast-context';
import useSessionStorageState from '../hooks/useSessionStorageState';



const ProjectDetailPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const history = useHistory();

  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<DmProject | null>(null);
  const [responseLLM, setResponseLLM] = useSessionStorageState<any>(`responseLLM-${projectId}`, null);
  const { showToast } = useToast();

  const fetchProject = async () => {
    const project = await getDmProject(projectId);
    setProject(project);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!project) return;

    try {
      const updatedProject = await updateDmProject(project.id, { status: newStatus });
      showToast({
        message: 'Project status updated successfully',
        severity: 'success',
      })
      setProject(updatedProject);
    } catch (error) {
      console.error('Failed to update project status:', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  // Placeholder data
  const projectName = project?.name
  const projectStatus = project?.status;

  const canUpdateStatus = DEAL_REVIEW_STATUSES.some(status => status.value === projectStatus);

  if (!project) return 0;

  const TABS = [
    { label: 'Documents', component: <DocumentsTab project={project} fetchProject={fetchProject} tab={tab} setTab={setTab} setResponseLLM={setResponseLLM} responseLLM={responseLLM} /> },
    { label: 'Generated Report', component: <GeneratedReportTab project={project} responseLLM={responseLLM} fetchProject={fetchProject} setTab={setTab} setResponseLLM={setResponseLLM} /> },
    { label: 'Deal Room', component: <DealRoomTab project={project} /> },
  ];

  return (
    <Container>
      <Box sx={{ p: 3, background: '#FAFCFF', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative' }}>
          <IconButton onClick={() => history.goBack()} sx={{ mr: 1 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="black" />
            </svg>
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, mr: 2 }}>
            Project name: {projectName}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canUpdateStatus ? (
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={projectStatus || ''}
                onChange={(e) => handleStatusChange(e.target.value)}
                sx={{
                  background: '#E5E5E5',
                  color: '#222',
                  fontWeight: 500,
                  fontSize: 16,
                  borderRadius: '16px',
                  height: 32,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 16px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#222',
                  },
                }}
              >
                {DEAL_REVIEW_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip
              label={mapDmProjectStatusToDealStatus(projectStatus || '')}
              sx={{
                background: '#E5E5E5',
                color: '#222',
                fontWeight: 500,
                fontSize: 16,
                borderRadius: '16px',
                px: 2,
                height: 32,
              }}
            />
          )}
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: '#E5E5E5', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            TabIndicatorProps={{ style: { background: '#222' } }}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 18,
                minHeight: 44,
                color: '#222',
                mr: 2,
              },
              '& .Mui-selected': {
                color: '#222',
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={t.label} label={t.label} />
            ))}
          </Tabs>
        </Box>
        <Box>
          {TABS[tab].component}
        </Box>
      </Box>
    </Container>
  );
};

export default ProjectDetailPage; 